import {ControlChange} from '../../../../core/models/control-change.model';
import {AdditionalInformationSectionConsts} from './additional-information-section.consts';
import {AdditionalInformationStateService} from '../../../../core/services/impl/section-services/additional-information/additional-information-state.service';
import {RegistrationService} from '../../../../core/services/impl/registration.service';
import {AfterViewInit, Component, OnInit} from '@angular/core';
import {SectionBase} from '../../../components/section/section-base';
import {AdditionalInformationSectionConfig} from './additional-information-section.config';
import {PredefinedValuesService} from '../../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import {PredefinedValuesStateService} from '../../../../core/services/impl/section-services/predefined-values/predefined-values-state.service';
import {TextFieldEditConfig} from '../../../components/presentation/text-field-edit/text-field-edit.component';
import {NavigateManagerService} from '../../../../core/services/navigate-manager.service';
import {OfferModificationsService} from '../../../../core/services/impl/offer-modifications/offer-modifications.service';
import {InsuranceCompanyCode} from '../../../models/insurance-company-code.enum';
import {isCarOne, isNext} from '../../../../core/utils/utils';
import {RegExpService} from '../../../services/reg-exp.service';
import {StepKey} from '../../../../core/enums/step-key.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';

@Component({
  selector: 'app-additional-information-section',
  templateUrl: './additional-information-section.component.html',
  styleUrls: ['./additional-information-section.component.scss']
})
export class AdditionalInformationSectionComponent extends SectionBase implements OnInit, AfterViewInit {

  vehicleTextFieldEditConfig: TextFieldEditConfig;
  insurancePeriodTextFieldEditConfig: TextFieldEditConfig;
  vehicleValueTextFieldEditConfig: TextFieldEditConfig;
  registrationNumberTextFieldEditConfig: TextFieldEditConfig;

  additionalInformationConfig: AdditionalInformationSectionConfig;
  public consts = AdditionalInformationSectionConsts;
  vehicleValueVisible: boolean;
  isNext = isNext();
  private vinFromSession: string;
  private vinInitialized: boolean = false;

  constructor(private registration: RegistrationService,
              private predefined: PredefinedValuesService,
              private predefinedState: PredefinedValuesStateService,
              private navigateManager: NavigateManagerService,
              private offerModificationsService: OfferModificationsService,
              private stateService: AdditionalInformationStateService,
              private regExpService: RegExpService) {
    super();
    this.stateService.initModeConfig();
    this.additionalInformationConfig = new AdditionalInformationSectionConfig(stateService, predefined, regExpService);
    if (!isNotNullOrUndefined(this.stateService.vehicleData.vin)) {
      this.initVinFromSession();
    }
  }

  get modeConfig(): any {
    return this.stateService.modeConfig;
  }

  get offerHasVehicleValue(): boolean {
    return !!this.offerModificationsService.getSelectedOfferVehicleValue();
  }

  get isBeesafe(): boolean {
    return this.offerModificationsService.stateService.insuranceCompanyCode === InsuranceCompanyCode.BEESAFE;
  }

  isVisible(key: string): boolean {
    return this.stateService.isVisible(key);
  }

  ngOnInit() {
    this.initVehicleValueVisible();
    this.initReadonlyFields();
    this.initBean();
    this.stateService.setInitialValues();
    this.initIsFieldDirtyBean();
    this.createConfig();
    this.buildForm();
    this.initControlsChanges();
  }

  ngAfterViewInit() {
    if (!this.vinInitialized && isNotNullOrUndefined(this.vinFromSession)) {
      this.getControl(this.consts.VIN).setValue(this.vinFromSession);
      this.vinInitialized = true;
    }
  }

  initBean() {
    this.bean = this.registration.vehicleData;
  }

  initControlsChanges() {
    const questionsControlChanges = this.initQuestionsState(this.stateService.questionFields);

    const nonQuestionsControlChanges = this.getNonQuestionsControlChanges(this.stateService.nonQuestionFields);

    this.handleControlsState(
      ...nonQuestionsControlChanges,
      ...questionsControlChanges
    );
  }

  createConfig() {
    this.formConfig = this.additionalInformationConfig.createConfig();
  }

  markAsDirty(event) {
    this.predefinedState.isDirtyBean = event;
  }

  edit() {
    this.navigateManager.moveToStep(StepKey.FIRST_STEP_1);
  }

  private async initVinFromSession() {
    const vin = await this.stateService.getVinFromSession();
    if (isNotNullOrUndefined(vin) && !this.vinInitialized && isNotNullOrUndefined(this.getControl(this.consts.VIN))) {
      this.getControl(this.consts.VIN).setValue(vin);
      this.vinInitialized = true;
    }
  }

  private initReadonlyFields() {
    this.vehicleTextFieldEditConfig = {
      fieldLabel: 'ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.vehicle',
      value: this.stateService.createVehicleInfoString(),
      actionLabel: 'edit',
      analyticsConfig: {
        skipFieldValue: true
      }
    };

    this.insurancePeriodTextFieldEditConfig = {
      fieldLabel: 'ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.insurancePeriod',
      value: this.stateService.createInsurancePeriodString(),
      actionLabel: 'edit'
    };

    if (this.vehicleValueVisible) {
      this.vehicleValueTextFieldEditConfig = {
        fieldLabel: 'ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.vehicleValue',
        value: this.offerModificationsService.getVehicleValueFormatted()
      };
    }

    if (!this.stateService.isRegistrationNumberUnknown) {
      this.registrationNumberTextFieldEditConfig = {
        fieldLabel: 'ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.registrationNumber',
        value: this.stateService.createRegistrationNumberString(),
        actionLabel: isCarOne() ? null : 'edit'
      }
    }
  }

  private initQuestionsState(fieldKeys: string[]): ControlChange[] {
    let controlChanges = [];
    fieldKeys.forEach(key => {
      if (this.isVisible(key)) {
        controlChanges.push({controlName: key, behaviour: value => this.stateService.changeQuestionAnswer(key, value)});
      }
    });
    return controlChanges;
  }

  private getNonQuestionsControlChanges(fieldKeys: string[]): ControlChange[] {
    let controlChanges = [];
    fieldKeys.forEach(key => {
      if (this.isVisible(key)) {
        switch (key) {
          case this.consts.VIN:
            controlChanges.push({controlName: key, behaviour: value => this.stateService.changeVinNumber(value)});
            break;
          case this.consts.TECHNICAL_INSPECTION_DATE:
            controlChanges.push({controlName: key, behaviour: value => this.stateService.changeTechnicalInspectionDate(value)});
            break;
        }
      }
    });
    return controlChanges;
  }

  private initIsFieldDirtyBean() {
    this.isDirtyBean = this.predefinedState.isDirtyBean;
  }

  private initVehicleValueVisible(): void {
    this.vehicleValueVisible = this.offerModificationsService.hasACOrACMini && this.offerHasVehicleValue;
  }

}
